import { CatalogCompanyType, CatalogItem, SupplierReview } from '@/shared/types/catalog';
import { PDPContentTab } from '@/shared/types/sourcing-marketplace';
import { getCatalogItemCustomizationArray } from '@/shared/utils/sourcing-marketplace-util';
import React, { useEffect, useRef, useState } from 'react';
import { PDP_DESCRIPTION_SECTION_ID, SAMPLE_QUANTITY_SELECTOR_ID } from '@/shared/constants/sourcing-marketplace';
import { SupplierReviewSummaryResponse } from '@/shared/types/api/catalog';
import { CategoryConfig, VerticalName } from '@/shared/types/category';
import { scrollToTop, scrollTo } from '@/shared/utils/tools';
import { useQueryParams } from '@/shared/hooks';
import dynamic from 'next/dynamic';
import { filter, flatten, map } from 'lodash-es';
import './index.less';
import SupplierBadges from '@/components/SourcingMarketplace/ProductDetail/SupplierBadges';
import ActionCards from '@/components/SourcingMarketplace/ProductDetail/ActionCards';
import ProductImagesSimpleSlide from '@/components/SourcingMarketplace/ProductDetail/ProductImagesSimpleSlide';
import ProductRunCard from '@/components/SourcingMarketplace/ProductDetail/ProductRunCard';
import cN from 'classnames';
import MobileBottomButtonGroup from '@/components/SourcingMarketplace/ProductDetail/MobileButtonGroup';
import ResponsiveContent from '@/components/Common/ResponsiveContent';
import SourcingBreadcrumbs from '../../Common/SourcingBreadcrumbs';

interface IProps {
  product: CatalogItem;
  reviews: SupplierReview[];
  reviewSummary: SupplierReviewSummaryResponse;
  categoryConfig: CategoryConfig;
  factorySpecialties: string[];
  isMobileURL?: boolean;
}

const NavigationPanel = dynamic(() => import('../NavigationPanel'));
const SupplierOverview = dynamic(() => import('../SupplierOverview'));
const ImageWithThumbnails = dynamic(() => import('../ImageWithThumbnails'));
const SampleBuilderSection = dynamic(() => import('../SampleBuilderSection'));
const ProductDetailsPanel = dynamic(() => import('../ProductDetailsPanel'));
const ProductDetailsHeader = dynamic(() => import('../ProductDetailsHeader'));

const PAGE_CONTAINER_CLASSNAME = 'content-container';

const ProductDetailsPage = ({
  product,
  reviews,
  reviewSummary,
  categoryConfig,
  factorySpecialties,
  isMobileURL,
}: IProps) => {
  const productRunRef = useRef<HTMLDivElement>(null);
  const [selectedTab, setSelectedTab] = useState<PDPContentTab>(PDPContentTab.PRODUCT_DESCRIPTION);
  const { queryParams } = useQueryParams<{}>();
  const sourcingBreadcrumbsRef = useRef(null);

  const isServices = product?.catalogCompany?.type === CatalogCompanyType.SPECIALIST;

  useEffect(() => {
    scrollToTop(`.${PAGE_CONTAINER_CLASSNAME}`);
  }, [queryParams]);

  const getCatalogItemImages = (): string[] => {
    const customizations = getCatalogItemCustomizationArray(product);
    const variantImages = map(
      flatten(
        map(
          filter(customizations, (c) => c.type === 'image_opts'),
          (c) => c.value,
        ),
      ),
      (value) => value.image,
    );
    return (product?.images || []).concat(variantImages);
  };

  const checkoutSection = (
    <SampleBuilderSection
      item={product}
      getPriceForVariant={() => product?.displayPrice}
      isVariantAvailable={() => true}
      showProductTitle={false}
      displayProductRunCard={productRunRef?.current ? true : false}
    />
  );

  const productPane = (
    <div className="product-content">
      <div className="hero">
        <h1 className="title">{product?.name}</h1>
        <div className="image-thumbnails">
          <ImageWithThumbnails images={getCatalogItemImages()} id={PDP_DESCRIPTION_SECTION_ID} alt="thumbnails" />
        </div>

        <SupplierBadges product={product} isProductLoading={false} factorySpecialties={factorySpecialties} />
      </div>
      <ResponsiveContent set={[[<ActionCards key="catalog-item-page-container-product-panel-action-cards" />, 1280]]} />
      {checkoutSection}
      <ProductDetailsPanel product={product} reviews={reviews} reviewSummary={reviewSummary} />
    </div>
  );

  return (
    <div className="catalog-item-page-container">
      <SourcingBreadcrumbs ref={sourcingBreadcrumbsRef} usedLocation="PDP" isMobileFromServer={isMobileURL} />
      {isMobileURL ? (
        <div className={cN(PAGE_CONTAINER_CLASSNAME, 'content-container-mobile')}>
          <SupplierOverview product={product} />
          <ProductImagesSimpleSlide images={product?.images} />
          <div className="title-box">
            <p className="title">{product?.name}</p>
          </div>

          {!isServices && <ProductRunCard ref={productRunRef} product={product} />}
          <SupplierBadges product={product} isProductLoading={false} factorySpecialties={factorySpecialties} />
          <ActionCards />
          {checkoutSection}
          <ProductDetailsPanel product={product} reviews={reviews} reviewSummary={reviewSummary} />
          <MobileBottomButtonGroup onClickSampleBtn={() => scrollTo(SAMPLE_QUANTITY_SELECTOR_ID)} product={product} />
        </div>
      ) : (
        <div className={cN(PAGE_CONTAINER_CLASSNAME, 'content-container-desktop')}>
          <NavigationPanel
            product={product}
            selectedTab={selectedTab}
            setSelectedTab={(tab: PDPContentTab) => setSelectedTab(tab)}
          />
          {productPane}
          <div className="right-box">
            {!isServices && <ProductRunCard ref={productRunRef} product={product} />}
            {checkoutSection}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetailsPage;
